import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Plus, Search, Filter, Download, Trash2, FileText } from "lucide-react";
import { toast } from "sonner";

const fmtCLP = (n) => "$" + Math.round(Number(n) || 0).toLocaleString("es-CL");

export default function Cotizaciones() {
  const navigate = useNavigate();
  const { api, isAdmin } = useAuth();
  const [cotizaciones, setCotizaciones] = useState([]);
  const [stats, setStats] = useState({ total: 0, borradores: 0, enviadas: 0, monto_total: 0 });
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ folio: "", cliente: "", estado: "", fecha_desde: "", fecha_hasta: "" });

  const buildParams = (f) => {
    const p = new URLSearchParams();
    if (f.folio) p.set("folio", f.folio);
    if (f.cliente) p.set("cliente", f.cliente);
    if (f.estado && f.estado !== "todos") p.set("estado", f.estado);
    if (f.fecha_desde) p.set("fecha_desde", f.fecha_desde);
    if (f.fecha_hasta) p.set("fecha_hasta", f.fecha_hasta);
    return p.toString();
  };

  const load = async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        api.get(`/api/cotizaciones?${buildParams(filters)}`),
        api.get("/api/cotizaciones/stats"),
      ]);
      setCotizaciones(r.data);
      setStats(s.data);
    } catch { toast.error("Error al cargar cotizaciones"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const uf = (f, v) => setFilters((p) => ({ ...p, [f]: v }));
  const clearFilters = () => {
    setFilters({ folio: "", cliente: "", estado: "", fecha_desde: "", fecha_hasta: "" });
    setTimeout(load, 50);
  };

  const downloadPdf = async (cot) => {
    try {
      const r = await api.get(`/api/cotizaciones/${cot.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${cot.folio}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Error al descargar PDF"); }
  };

  const remove = async (cot) => {
    if (!window.confirm(`¿Eliminar la cotización ${cot.folio}?`)) return;
    try { await api.delete(`/api/cotizaciones/${cot.id}`); toast.success("Cotización eliminada"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error al eliminar"); }
  };

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      <header className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText className="h-5 w-5 text-[#0FB4B7]" />
            <span className="text-sm font-semibold text-[#384091]">Cotizaciones</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)}>
              <Filter className="h-3.5 w-3.5 mr-1" /> Filtros
            </Button>
            <Button size="sm" className="bg-[#0FB4B7] hover:bg-[#0D999C] text-white"
              onClick={() => navigate("/tecnico/cotizaciones/nueva")}>
              <Plus className="h-4 w-4 mr-1" /> Nueva
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate("/tecnico/dashboard")}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-4 space-y-4">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "Total", value: stats.total, color: "#384091" },
            { label: "Borradores", value: stats.borradores, color: "#D97706" },
            { label: "Enviadas", value: stats.enviadas, color: "#059669" },
            { label: "Monto total", value: fmtCLP(stats.monto_total), color: "#0FB4B7" },
          ].map((s) => (
            <Card key={s.label} className="border-0 shadow-sm">
              <CardContent className="p-3 text-center">
                <p className="text-lg font-bold" style={{ color: s.color }}>{s.value}</p>
                <p className="text-xs text-gray-500">{s.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters */}
        {showFilters && (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 space-y-3">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div><Label className="text-xs">Folio</Label><Input value={filters.folio} onChange={(e) => uf("folio", e.target.value)} className="h-9 text-xs" placeholder="COT-..." /></div>
                <div><Label className="text-xs">Cliente</Label><Input value={filters.cliente} onChange={(e) => uf("cliente", e.target.value)} className="h-9 text-xs" placeholder="Buscar..." /></div>
                <div><Label className="text-xs">Estado</Label>
                  <Select value={filters.estado} onValueChange={(v) => uf("estado", v)}>
                    <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Todos" /></SelectTrigger>
                    <SelectContent><SelectItem value="todos">Todos</SelectItem><SelectItem value="borrador">Borrador</SelectItem><SelectItem value="enviada">Enviada</SelectItem></SelectContent>
                  </Select></div>
                <div><Label className="text-xs">Fecha desde</Label><Input type="date" value={filters.fecha_desde} onChange={(e) => uf("fecha_desde", e.target.value)} className="h-9 text-xs" /></div>
                <div><Label className="text-xs">Fecha hasta</Label><Input type="date" value={filters.fecha_hasta} onChange={(e) => uf("fecha_hasta", e.target.value)} className="h-9 text-xs" /></div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" className="bg-[#0FB4B7] text-white" onClick={load}><Search className="h-3.5 w-3.5 mr-1" /> Buscar</Button>
                <Button variant="outline" size="sm" onClick={clearFilters}>Limpiar</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* List */}
        {loading ? (
          <p className="text-center text-gray-400 py-12">Cargando...</p>
        ) : cotizaciones.length === 0 ? (
          <p className="text-center text-gray-400 py-12">Sin cotizaciones</p>
        ) : (
          <div className="space-y-2">
            {cotizaciones.map((c) => (
              <Card key={c.id} className="border-0 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-4 flex items-center justify-between gap-3">
                  <Link to={`/tecnico/cotizaciones/${c.id}`} className="flex items-center gap-3 flex-1 min-w-0">
                    <FileText className="h-4 w-4 text-[#0FB4B7] flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">
                        {c.folio} — {c.cliente_nombre}
                      </p>
                      <p className="text-xs text-gray-500">
                        {c.fecha} · {c.items?.length || 0} ítem(s) · {fmtCLP(c.totales?.total)}
                      </p>
                    </div>
                  </Link>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <Badge className={c.estado === "enviada" ? "bg-emerald-50 text-emerald-700 border-0" : "bg-amber-50 text-amber-700 border-0"}>
                      {c.estado === "enviada" ? "Enviada" : "Borrador"}
                    </Badge>
                    <Button variant="ghost" size="sm" onClick={() => downloadPdf(c)} title="Descargar PDF">
                      <Download className="h-4 w-4" />
                    </Button>
                    {isAdmin && (
                      <Button variant="ghost" size="sm" onClick={() => remove(c)} title="Eliminar"
                        className="text-red-400 hover:text-red-600">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
